/**
 * /onboarding — premium standalone-flow для першого налаштування.
 * Стиль: Home.tsx (editorial concept). Мінімум emoji, замість них —
 * іконки + scribble-decoration + font-display/serif/mono.
 *
 * 3 фази:
 *   STEP 1 (select)   — обрати сфери
 *   STEP 2 (progress) — рядки з'являються ОДИН ЗА ОДНИМ
 *                        (texts.py:5125 search_vac_demo_texts_list)
 *   STEP 3 (done)     — підсумок та CTA «Отримати вакансії»
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "@/api/client";
import { showAppAlert } from "@/lib/appAlert";
import { useCategories, useMe } from "@/api/hooks";
import { AuraBackground } from "@/components/AuraBackground";
import { SelectionReady } from "@/components/SelectionReady";
import { SpheresPicker } from "@/components/SpheresPicker";
import { StickyAction } from "@/components/StickyAction";
import { Icon } from "@/components/Icon";
import { InflowLogo } from "@/components/InflowLogo";
import { ScribbleStar, ScribbleUnderline } from "@/components/Icon";
import { Spinner } from "@/components/Spinner";
import { tg } from "@/lib/telegram";

// 1:1 з 1.0 (texts.py:5125). Без emoji — статус малюється іконкою.
const PROGRESS_STEPS = [
  "Починаю шукати вакансії для тебе",
  "Визначаю ключові джерела пошуку",
  "Перевіряю 295 тематичних чатів",
  "Перевіряю 178 профільних каналів",
  "Аналізую вакансії у Threads",
  "Аналізую freelance-біржі",
  "Фільтрую вакансії за актуальністю",
  "Перевіряю підозрілі пропозиції",
  "Перевіряю вимоги та деталі",
  "Готую добірку найкращих вакансій",
];
const STEP_DELAY_MS = 1568; // прискорено на 20% (було 1960) — юзер 2026-06-10

type Phase = "select" | "progress" | "done";



interface PreviewResponse {
  total_24h: number;
  selected_category_names: string[];
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useMe();

  // Захист від повторного онбордингу: стара inline-кнопка «Налаштувати бот»
  // у чаті веде ПРЯМО на /onboarding. Якщо юзер уже пройшов онбординг на
  // момент ВІДКРИТТЯ — ведемо на головну (юзер 2026-06-11: «етап
  // налаштування можна пройти безліч раз через стару кнопу»). Перевірка
  // ОДНОРАЗОВА на mount: при живому проходженні onboarding_completed
  // флипається пізніше (після збереження категорій) і цей guard не зачіпає.
  const completedCheckedRef = useRef(false);
  useEffect(() => {
    if (!me.data || completedCheckedRef.current) return;
    completedCheckedRef.current = true;
    if (me.data.onboarding_completed && !me.data.is_admin) {
      navigate("/", { replace: true });
    }
  }, [me.data, navigate]);

  // Початкова фаза: якщо НЕ підписаний на канал → channel-step, інакше select.
  // useState(callback) щоб me.data перевірити лише раз при mount; подальші
  // зміни me.data → guard сам редиректить, OnboardingPage не реагує.
  // Owner 2026-09-09: кроку «Крок 1 з 2: підписка» більше немає. Людина
  // одразу обирає сфери, а підписку ми питаємо на кнопці «Отримати підбірку»
  // — тобто вже після того, як вона побачила, що саме її чекає.
  const [phase, setPhase] = useState<Phase>("select");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [revealedCount, setRevealedCount] = useState(0); // скільки рядків вже з'явилось
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Safe-area для fullscreen (юзер 2026-05-22: «кнопки Telegram перебивають
  // верх онбордингу»). Читаємо WebApp.contentSafeAreaInset.top.
  const [topInset, setTopInset] = useState<number>(0);

  const cats = useCategories();
  const categories = useMemo(() => cats.data?.items ?? [], [cats.data]);

  useEffect(() => {
    const wa = tg() as any;
    wa?.BackButton?.hide?.();
    wa?.expand?.();
    const readInset = () => {
      const csai = wa?.contentSafeAreaInset?.top;
      const sai = wa?.safeAreaInset?.top;
      // Адитивно (як useTopInset): системний інсет + інсет під кнопками
      // Telegram. Раніше Math.max недобирав → кнопки перекривали верх.
      const safe = typeof sai === "number" ? sai : 0;
      const content = typeof csai === "number" ? csai : 0;
      setTopInset(safe + content);
    };
    readInset();
    try {
      wa?.onEvent?.("safeAreaChanged", readInset);
      wa?.onEvent?.("contentSafeAreaChanged", readInset);
      wa?.onEvent?.("fullscreenChanged", readInset);
    } catch {}
    return () => {
      try {
        wa?.offEvent?.("safeAreaChanged", readInset);
        wa?.offEvent?.("contentSafeAreaChanged", readInset);
        wa?.offEvent?.("fullscreenChanged", readInset);
      } catch {}
    };
  }, []);

  // ScrollToTop при зміні phase (юзер 2026-05-22: «нажав зберегти
  // знизу — друга сторінка прыгнула посередині»).
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [phase]);

  // STEP 2 — кожні STEP_DELAY_MS додаємо ще один рядок.
  // Юзер 2026-05-24: «передостанній і останній кроки з'являються
  // одночасно». Корінь: попередня реалізація використовувала cascading
  // setTimeout який міг race'нути з useEffect re-runs після setState.
  // Замінив на ОДИН setInterval — фіксований інтервал, без drift.
  useEffect(() => {
    if (phase !== "progress") return;
    const interval = setInterval(() => {
      setRevealedCount((c) => {
        if (c >= PROGRESS_STEPS.length) {
          clearInterval(interval);
          return c;
        }
        tg()?.HapticFeedback?.selectionChanged?.();
        return c + 1;
      });
    }, STEP_DELAY_MS);
    return () => clearInterval(interval);
  }, [phase]);

  // Коли всі рядки з'явились → preview, перехід на done.
  // Юзер 2026-06-10: підбірку БІЛЬШЕ НЕ шлемо в бот (бот-teaser прибрано).
  // Уся цінність — у стрічці Mini App: юзер тисне «Подивитись вакансії» і
  // потрапляє на /feed. Тому /onboarding/finalize НЕ викликаємо.
  useEffect(() => {
    if (phase !== "progress" || revealedCount < PROGRESS_STEPS.length) return;
    const t = setTimeout(async () => {
      try {
        const data = await apiFetch<PreviewResponse>("/onboarding/preview");
        setPreview(data);
        setPhase("done");
        tg()?.HapticFeedback?.notificationOccurred?.("success");
        // Invalidate /me щоб OnboardingRedirectGuard зрозумів completed=true.
        qc.invalidateQueries({ queryKey: ["me"] });
      } catch (e) {
        setError((e as Error).message);
      }
    }, 800);
    return () => clearTimeout(t);
  }, [phase, revealedCount]);

  const toggle = (id: number) => {
    tg()?.HapticFeedback?.selectionChanged?.();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    if (selected.size === 0) {
      showAppAlert("Обери хоча б одну сферу");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await apiFetch("/onboarding/save-categories", {
        method: "POST",
        body: { category_ids: [...selected] },
      });
      setPhase("progress");
      setRevealedCount(0);
      tg()?.HapticFeedback?.impactOccurred?.("medium");
    } catch (e) {
      setError((e as Error).message);
      tg()?.HapticFeedback?.notificationOccurred?.("error");
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = () => {
    // Юзер 2026-06-10: після онбордингу ведемо в СТРІЧКУ Mini App. Промо (якщо
    // прийшов по промо-посиланню) НЕ губиться — показуємо банером зверху
    // стрічки (pending_promo лишається в localStorage).
    //
    // Owner 2026-09-09: якщо доступ до підбірки ще закритий — ведемо на екран
    // замків, а не в стрічку. Там людина бачить свої справжні вакансії й
    // кнопку підписатись.
    // Owner 2026-09-09: якщо доступ ще закритий — ведемо на продаюче вікно,
    // а не в порожню стрічку. Саме там людині пояснюють обмін.
    navigate(me.data?.selection_unlocked === false ? "/unlock" : "/feed", {
      replace: true,
    });
  };

  if (cats.isLoading) {
    return (
      <>
        <AuraBackground />
        <div
          className="relative min-h-screen flex items-center justify-center"
          style={{ paddingTop: topInset }}
        >
          <Spinner />
        </div>
      </>
    );
  }

  // ────────── STEP 0: channel subscribe ──────────
  // Pre-step онбордингу: юзер ще не підписаний на канал (або флаг null
  // після reset). Показуємо channel-CTA + кнопку «Я підписався».

  // ────────── STEP 1: select ──────────
  if (phase === "select") {
    return (
      <>
        <AuraBackground />
        <div
          className="relative min-h-screen pb-40"
          style={{ paddingTop: topInset }}
        >
        {/* Header */}
        <div className="px-5 pt-8 pb-2 flex justify-between items-center">
          <InflowLogo size={18} />
          <div className="font-mono text-[0.625rem] uppercase tracking-[0.12em] text-ink-dim">
            налаштування
          </div>
        </div>

        {/* Hero */}
        <div className="relative px-5 pt-6 pb-4">
          <h1
            className="font-display font-bold text-ink"
            style={{
              fontSize: "2.625rem",
              lineHeight: 0.98,
              letterSpacing: "-0.03em",
            }}
          >
            Налаштуй
            <br />
            <span className="relative inline-block">
              <span className="accent-italic" style={{ fontSize: "2.75rem", fontWeight: 600 }}>
                свої сфери
              </span>
              <ScribbleUnderline width={210} className="absolute -bottom-2.5 -left-1" />
            </span>
          </h1>
          <div className="absolute right-5 top-7">
            <ScribbleStar size={28} />
          </div>
          <p className="mt-5 text-[0.875rem] leading-relaxed text-ink-dim">
            Обери напрямки, в яких ти шукаєш клієнтів. Бот надсилатиме лише
            релевантні вакансії з 400+ digital-каналів, freelance-бірж і Threads.
          </p>
        </div>

        <div className="px-5">
          <SpheresPicker
            items={categories}
            selected={selected}
            onToggle={toggle}
            onSelectAll={() => setSelected(new Set(categories.map((c) => c.id)))}
            onClear={() => setSelected(new Set())}
          />
        </div>

        {error && (
          <div className="mx-6 mt-4 text-sm text-red-500">{error}</div>
        )}

        <StickyAction
          label="Зберегти і налаштувати"
          pendingLabel="Зберігаю…"
          pending={saving}
          disabled={selected.size === 0}
          onClick={handleSave}
          fixed
        />
        </div>
      </>
    );
  }

  // ────────── STEP 2: progressive reveal ──────────
  if (phase === "progress") {
    return (
      <>
        <AuraBackground />
        <div
          className="relative min-h-screen px-5 pt-12 pb-10"
          style={{ paddingTop: topInset + 48 }}
        >
        <div className="flex items-center gap-2 font-mono text-[0.6875rem] uppercase tracking-[0.12em] text-ink-dim">
          <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
          триває налаштування
        </div>
        <h1
          className="mt-3 font-display font-bold text-ink"
          style={{ fontSize: "2.75rem", lineHeight: 1.02, letterSpacing: "-0.03em" }}
        >
          Збираю
          <br />
          <span className="accent-italic" style={{ fontWeight: 600 }}>
            твою добірку.
          </span>
        </h1>

        <div className="mt-12 space-y-4">
          {PROGRESS_STEPS.slice(0, revealedCount).map((step, idx) => {
            const isLast = idx === revealedCount - 1;
            const isDone = idx < revealedCount - 1 || revealedCount >= PROGRESS_STEPS.length;
            return (
              <div
                key={idx}
                className="flex items-center gap-4 animate-fade-in-up"
                style={{
                  animation: "fadeInUp 0.55s ease-out both",
                }}
              >
                <div
                  className={
                    "relative grid h-8 w-8 shrink-0 place-items-center rounded-full transition " +
                    (isDone
                      ? "bg-primary text-dark"
                      : "bg-transparent")
                  }
                >
                  {isDone ? (
                    <Icon name="check" size={14} color="#FFFFFF" />
                  ) : (
                    /* Spinning ring */
                    <svg
                      className="animate-spin h-7 w-7 text-primary"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="9"
                        stroke="currentColor"
                        strokeOpacity="0.18"
                        strokeWidth="2.5"
                      />
                      <path
                        d="M21 12a9 9 0 00-9-9"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  )}
                </div>
                <div
                  className={
                    "text-[1rem] leading-snug " +
                    (isLast && !isDone
                      ? "text-ink font-medium"
                      : "text-ink-dim")
                  }
                >
                  {step}
                </div>
              </div>
            );
          })}
        </div>

        <style>{`
          @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(8px); }
            to   { opacity: 1; transform: translateY(0); }
          }
        `}</style>
        </div>
      </>
    );
  }

  // ────────── STEP 3: done ──────────
  // Якщо юзер прийшов по промо — фінальний CTA веде на активацію промокоду
  // (підбірка вже піде в бот), а не просто закриває аппу (юзер 2026-06-09).
  const pendingPromo = localStorage.getItem("pending_promo");
  return (
    <SelectionReady
      total={preview?.total_24h ?? 0}
      topInset={topInset}
      onPrimary={handleFinalize}
      caption={
        pendingPromo ? (
          <>
            Відкривай добірку вакансій, і там зможеш активувати свій промокод{" "}
            <Icon
              name="gift"
              size={14}
              color="#FE6B04"
              className="inline-block align-text-bottom"
            />
          </>
        ) : undefined
      }
    />
  );
}
